// match.mjs — Arc 1 (notes → homework) template matcher.
//
// PURE. No DOM, no network, no Supabase, no imports. Templates arrive already fetched.
// SC-A3 wires the fetch (on modal-open only) and the prefill; this file only decides
// which templates a note text points at.
//
// Public API:
//   matchTemplates(noteText, exerciseTemplates, homeworkTemplates, cueList?) -> {
//     cueSentences:   [String],                                   // sentences carrying a goal-language cue
//     homeworkMatches:[{ templateId, score, matchedOn, sentence }], // homework_templates (title)
//     exerciseMatches:[{ templateId, score, matchedOn, sentence }]  // exercise_templates (name)
//   }
//   exerciseTemplates: [{ id, name, ... }]   homeworkTemplates: [{ id, title, ... }]
//   cueList: optional [String] of cue phrases; defaults to DEFAULT_CUES (EN + HE).
//
// MATCHING — Tiers 1 + 2 only (locked: no AI, no embeddings, no fuzzy edit-distance).
//
// Tier 1 (normalise, both sides):
//   - strip niqqud U+0591–U+05C7 and geresh/gershayim U+05F3/05F4
//   - normalise Hebrew final forms  ך ם ן ף ץ  ->  כ מ נ פ צ
//   - casefold; strip punctuation; collapse whitespace; tokenise on whitespace
//   - tolerate a single Hebrew prefix letter (ו ה ב ל ש כ מ) on the LEADING word of a
//     template name when searching the note (e.g. note "הלשון" matches name-leading "לשון")
//
// Tier 2 (light, rule-based stemming applied to every token on both sides — never a library):
//   - English:  strip -ing / -ed (len guards) with a doubling collapse (running->runn->run),
//               else strip a plural: sibilant "…{s,x,z,ch,sh}es" -> drop "es", else trailing
//               "s" (not "ss") -> drop "s".
//   - Hebrew:   strip ONE plural suffix. NOTE: finals are normalised FIRST, so the ־ים plural
//               ending arrives as "ימ" (mem de-finalised). Strip "ימ" or "ות" when >=2 stem
//               letters remain.  בועות -> בוע ,  בלונים -> בלונ .
//   The stemmer's ONLY invariant is that it is the SAME function on both sides — stems may be
//   ugly (this->thi, need->ne); that is harmless because we compare stem-to-stem. Do not add
//   guards to prettify stems.
//
// Match rule: a template matches when its stemmed name tokens appear as a CONSECUTIVE window
// inside the note's stemmed tokens (token-sequence containment, not character-substring —
// "art" never matches inside "start"; "with attach" never matches "without attachment").
//
// Cue phrases run through the IDENTICAL normalise->stem->token-window path (NOT substring),
// so "work on" does not fire inside "network online". A sentence containing any cue is a cue
// sentence; a template match found in a cue sentence scores higher.
//
// score    = 1 + 0.5*(nameTokenCount - 1) + (inCueSentence ? 1 : 0)   [best sentence wins per template]
// matchedOn = the surface note window (normalised tokens, space-joined) that triggered the match
// sentence  = the original (trimmed) note sentence the match was found in
// Each list is deduped by templateId (best score kept), sorted by score desc then id, capped at 8.

// Hebrew prefix letters tolerated on a template name's leading word (base forms; finals already normalised).
const HE_PREFIX = new Set(['ו', 'ה', 'ב', 'ל', 'ש', 'כ', 'מ']);

const HE_FINALS = { 'ך': 'כ', 'ם': 'מ', 'ן': 'נ', 'ף': 'פ', 'ץ': 'צ' };

// Built-in cue list of generic goal-language markers (matching input, not display text).
//
// LOCKED (Eytan, A2 rulings): the cue list is RANKING-ONLY. It NEVER gates. A template match
// is never hidden, suppressed, or dropped for lacking a cue sentence — cues only add a scoring
// nudge (see `score`). It is an internal ranking signal, not configuration, and is deletable at
// zero behavioural cost (matches would only re-rank, never disappear). No clinic-specific
// phrases: matching is template-name-driven and generic.
export const DEFAULT_CUES = [
  // English
  'at home', 'practice', 'practise', 'practicing', 'practising',
  'times a day', 'work on', 'homework', 'every day',
  // Hebrew
  'בבית', 'לתרגל', 'תרגול', 'פעמים ביום', 'פעמיים ביום', 'לעבוד על', 'שיעורי בית', 'כל יום',
];

const isHeb = (ch) => ch >= '֐' && ch <= '׿';
const isVowel = (ch) => 'aeiou'.includes(ch);

function normalize(text) {
  if (!text) return '';
  let out = '';
  for (const ch of String(text)) {
    // strip niqqud + geresh/gershayim
    if (ch >= '֑' && ch <= 'ׇ') continue;
    if (ch === '׳' || ch === '״') continue;
    out += HE_FINALS[ch] || ch;
  }
  out = out.toLowerCase();
  // punctuation -> space: keep ASCII word chars and Hebrew letters, drop the rest
  let cleaned = '';
  for (const ch of out) {
    if ((ch >= 'a' && ch <= 'z') || (ch >= '0' && ch <= '9') || isHeb(ch)) cleaned += ch;
    else cleaned += ' ';
  }
  return cleaned.replace(/\s+/g, ' ').trim();
}

export function tokenize(text) {
  const n = normalize(text);
  return n ? n.split(' ') : [];
}

// Collapse a trailing doubled consonant (running->runn->run), EXCEPT l/s/f/z, which stay
// doubled in the base word (roll/pull/tell/press/kiss/stuff/buzz) — so the -ing/-ed form and
// the base agree. Accepted new rare miss: controlling->controll != control.
const KEEP_DOUBLE = new Set(['l', 's', 'f', 'z']);
function collapseDouble(base) {
  const last = base[base.length - 1];
  if (base.length >= 3 && last === base[base.length - 2] && !isVowel(last) && !KEEP_DOUBLE.has(last)) {
    return base.slice(0, -1);
  }
  return base;
}

function stemEnglish(tok) {
  if (tok.length >= 5 && tok.endsWith('ing')) return collapseDouble(tok.slice(0, -3));
  if (tok.length >= 4 && tok.endsWith('ed')) return collapseDouble(tok.slice(0, -2));
  if (tok.length > 4 && /(ses|xes|zes|ches|shes)$/.test(tok)) return tok.slice(0, -2);
  if (tok.length > 3 && tok.endsWith('s') && !tok.endsWith('ss')) return tok.slice(0, -1);
  return tok;
}

function stemHebrew(tok) {
  // finals already normalised upstream, so the ־ים plural arrives as "ימ"
  if (tok.endsWith('ימ') && tok.length - 2 >= 2) return tok.slice(0, -2);
  if (tok.endsWith('ות') && tok.length - 2 >= 2) return tok.slice(0, -2);
  return tok;
}

export function stem(tok) {
  if (!tok) return tok;
  return isHeb(tok[0]) ? stemHebrew(tok) : stemEnglish(tok);
}

const stemAll = (toks) => toks.map(stem);

// Are two template names the SAME name? Normalise→stem both, compare token sequences,
// tolerating one Hebrew prefix letter on either side's leading token (same rule the matcher
// applies note-side). Used by SC-A3 same-name suppression so an exercise suggestion is not
// double-added when the accepted homework template already contains it. One invariant, both sides.
export function namesEquivalent(a, b) {
  const ra = tokenize(a);
  const rb = tokenize(b);
  const sa = stemAll(ra);
  const sb = stemAll(rb);
  if (!sa.length || !sb.length || sa.length !== sb.length) return false;
  for (let i = 0; i < sa.length; i++) {
    if (sa[i] === sb[i]) continue;
    if (i === 0) {
      if (ra[0] && ra[0].length > 2 && HE_PREFIX.has(ra[0][0]) && stem(ra[0].slice(1)) === sb[0]) continue;
      if (rb[0] && rb[0].length > 2 && HE_PREFIX.has(rb[0][0]) && stem(rb[0].slice(1)) === sa[0]) continue;
    }
    return false;
  }
  return true;
}

// Does the stemmed name window appear consecutively in the note?
// prefixTol: allow one Hebrew prefix letter on the note token aligned with the name's FIRST token.
function windowMatch(noteNorm, noteStem, nameNorm, nameStem, prefixTol) {
  const nLen = nameStem.length;
  if (nLen === 0 || noteStem.length < nLen) return -1;
  for (let start = 0; start + nLen <= noteStem.length; start++) {
    let ok = true;
    for (let j = 0; j < nLen; j++) {
      const nt = noteStem[start + j];
      if (nt === nameStem[j]) continue;
      if (prefixTol && j === 0) {
        const raw = noteNorm[start]; // unstemmed, for prefix strip
        if (raw && raw.length > 2 && HE_PREFIX.has(raw[0]) && stem(raw.slice(1)) === nameStem[0]) continue;
      }
      ok = false;
      break;
    }
    if (ok) return start; // index of the matching window
  }
  return -1;
}

// Split into sentences on terminal punctuation and newlines; keep original (trimmed) text.
function splitSentences(text) {
  return String(text || '')
    .split(/[.!?\n\r;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function buildCue(cueList) {
  return (cueList && cueList.length ? cueList : DEFAULT_CUES).map((phrase) => {
    const norm = tokenize(phrase);
    return { norm, stem: stemAll(norm) };
  });
}

function matchOneList(templates, nameKey, sentences, cueFlags) {
  const out = [];
  for (const t of templates || []) {
    if (!t || t.id == null) continue;
    const raw = t[nameKey] != null ? t[nameKey] : (t.name != null ? t.name : t.title);
    const nameNorm = tokenize(raw);
    const nameStem = stemAll(nameNorm);
    if (!nameStem.length) continue;

    let best = null;
    for (let si = 0; si < sentences.length; si++) {
      const s = sentences[si];
      const idx = windowMatch(s.norm, s.stem, nameNorm, nameStem, /*prefixTol*/ true);
      if (idx < 0) continue;
      const inCue = cueFlags[si];
      const score = 1 + 0.5 * (nameStem.length - 1) + (inCue ? 1 : 0);
      if (!best || score > best.score) {
        best = {
          templateId: t.id,
          score,
          matchedOn: s.norm.slice(idx, idx + nameStem.length).join(' '),
          sentence: s.text,
        };
      }
    }
    if (best) out.push(best);
  }
  out.sort((a, b) => b.score - a.score || String(a.templateId).localeCompare(String(b.templateId)));
  return out.slice(0, 8);
}

export function matchTemplates(noteText, exerciseTemplates, homeworkTemplates, cueList) {
  const sentenceTexts = splitSentences(noteText);
  const cues = buildCue(cueList);

  // Per-sentence token cache + cue flag.
  const sentences = sentenceTexts.map((text) => {
    const norm = tokenize(text);
    return { text, norm, stem: stemAll(norm) };
  });
  const cueFlags = sentences.map((s) =>
    cues.some((c) => c.stem.length && windowMatch(s.norm, s.stem, c.norm, c.stem, /*prefixTol*/ false) >= 0)
  );
  const cueSentences = sentences.filter((_, i) => cueFlags[i]).map((s) => s.text);

  return {
    cueSentences,
    homeworkMatches: matchOneList(homeworkTemplates, 'title', sentences, cueFlags),
    exerciseMatches: matchOneList(exerciseTemplates, 'name', sentences, cueFlags),
  };
}
